import React, { useEffect, useState } from "react";
import { FleetService } from 'services';
import { Intent } from "@blueprintjs/core";
import PropTypes from "prop-types";
import { NotificationToaster } from "components/notifications";
import { Dropdown } from "components/dropdown";
import { InstantEventButton } from "components/events";
import { ShowHide } from "components/layout";

export function EventList({
    assetId = 0,
    periodType = "day",
    eventDate = "",
    popoverUsePortal = true,
    onEventCreated = () => {},
    compact = false,
    disabled = false,
    loading = false
}) {
    const [eventTypeList, setEventTypeList] = useState([]);
    const [eventTypes, setEventTypes] = useState([]);
    const [eventCreating, setEventCreating] = useState(false);

    useEffect(() => {
        fetchAssetEventTypes();
    }, []);

    useEffect(() => {
        if (!eventTypeList || !assetId || !eventDate) {
            return;
        }

        const eventTypeButtons = eventTypeList.map((x) => {
            return (
                <InstantEventButton 
                    buttonType="MenuItem" 
                    key={x.id} 
                    id={x.id.toString()} 
                    text={x.name} 
                    icon={<i className={`custom-icon custom-icon-${x.icon}`} ></i>} 
                    onEventCreate={() => onEventCreate(x.id)}
                    eventTypeId={x.eventTypeId} 
                    intent=""
                    buttonDisabled={eventCreating}
                />
            );
        });

        setEventTypes(eventTypeButtons);
    }, [eventTypeList, assetId, eventDate]);

    function fetchAssetEventTypes() {
        FleetService.getEventTypesForAsset(assetId, true)
            .then((response) => {
                const formattedEventTypes = response.filter(x => x.canCreateEvent).map((x) => {
                    return { "id": x.eventTypeId, "name": x.eventTypeName, "icon": x.iconName };
                });

                setEventTypeList(formattedEventTypes);
            }).catch((error) => {
                NotificationToaster.show(Intent.DANGER, `Failed to get Event Types. ${error}`);
            });
    }

    function onEventCreate(eventTypeId) {
        setEventCreating(true);

        const eventCreateRequest = {
            eventTypeId: eventTypeId,
            assetId: assetId,
            startDate: eventDate
        }

        FleetService.createEvent(eventCreateRequest)
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, "Event created successfully.");
                
                if (onEventCreated) {
                    onEventCreated();
                }
            })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, `Failed to create event. ${error}`);
            })
            .finally(() => {
                setEventCreating(false);
            });
    }

    const eventTypeDropdownPreamble = (
        <>
            <h4>Ad-hoc Event Types</h4>
            <ShowHide
                evaluator={periodType === "day"}
                show={(<p>The selected day will be used as the event's date for day detail view.</p>)}
                hide={(<p>The period's start date will be used as the event's date for week and month detail views.</p>)}
            />
        </>
    );

    return (
        <Dropdown 
            intent="primary"
            text={compact ? "" : "Ad-hoc Event"}
            icon={compact ? "add" : null}
            large={!compact}
            iconOnly={compact}
            id="event-types"
            items={eventTypes}
            preamble={eventTypeDropdownPreamble}
            disabled={eventCreating || disabled}
            popoverUsePortal={popoverUsePortal}
            loading={loading}
        />
    );
}

EventList.propTypes = {
    assetId: PropTypes.number,
    periodType: PropTypes.string,
    eventDate: PropTypes.string,
    popoverUsePortal: PropTypes.bool,
    onEventCreated: PropTypes.func,
    compact: PropTypes.bool,
    disabled: PropTypes.bool,
    loading: PropTypes.bool
};